import { useState, useEffect, useMemo } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import { useSlugFlow } from "hooks/useSlugFlow";
import SoftInput from "components/SoftInput";
import { useAuth0 } from "@auth0/auth0-react";
import DataTable from "cxfcomponents/Tables/DataTable";
import { useConnectorsWithCreator } from "hooks/useConnectorsWithCreator";
import { useOwnedConnectors } from "hooks/useOwnedConnectors";
import SoftTypography from "components/SoftTypography";
import { Link } from "react-router-dom";
import moment from "moment";
import StatusBadge from "cxfcomponents/StatusBadge";
import { AddConnectorDialog } from "./addConnectorDialog";
import defaultConnectorIcon from "assets/images/connector-list-view.svg";
import { PERMISSIONS } from "constants/portal";
import { useAllowedPermissions } from "hooks/useAllowedPermissions";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import { useResourceDependencies } from "hooks/useResourceDependencies";

export const Connectors = () => {
  const { user } = useAuth0();
  const { flow } = useSlugFlow();
  const [searchName, setSearchName] = useState("");
  const { connectorsWithCreator, connectorCreators, handleFetchConnectorsWithCreators } =
    useConnectorsWithCreator();
  const { ownedConnectors: ownedComponents } = useOwnedConnectors();
  const [openAddConnectorDialog, setOpenAddConnectorDialog] = useState(false);
  const { isAllowedAction } = useAllowedPermissions();
  const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(flow?.id);
  const { dependencies: flowConnectorIds } = useResourceDependencies({
    sourceId: flow?.id,
    direction: "forward",
    targetResourceType: "connector"
  });

  const handleAddConnector = async () => {
    setOpenAddConnectorDialog(true);
  };

  const handleFilterConnector = (connector) => {
    return connector.name?.toLowerCase()?.includes(searchName.toLowerCase());
  };

  const tableData = useMemo(() => {
    const flowConnectors = connectorsWithCreator.filter((connector) =>
      flowConnectorIds.includes(connector.id)
    );

    return {
      columns: [
        {
          Header: "developer_logo",
          accessor: "developer_logo",
          show: false,
          canGroupBy: false,
          disableSortBy: true,
        },
        {
          Header: "logo",
          accessor: "logo",
          canGroupBy: false,
          disableSortBy: true,
          Cell: ({ value }) => (
            <SoftBox width="60px" height="60px">
              {value}
            </SoftBox>
          ),
        },
        {
          Header: "name",
          accessor: "name",
          width: 250,
          canGroupBy: false,
          Cell: ({ row, value }) => (
            <SoftTypography fontSize="14px" fontWeight="bold">
              <Link
                style={{ color: "#2D3748" }}
                className="white-to-dark"
                to={`/connector/${row.original.id}/${flow.id}`}
              >
                {value}
              </Link>
            </SoftTypography>
          ),
        },
        {
          Header: "developer",
          accessor: "developer",
          width: 250,
          canGroupBy: false,
          Cell: ({ row, value }) => (
            <SoftBox
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              {row.values.developer_logo}
              <SoftTypography
                marginLeft="2px"
                fontWeight="bold"
                fontSize="12px"
              >
                {value}
              </SoftTypography>
            </SoftBox>
          ),
        },
        {
          Header: "status",
          accessor: "status",
          canGroupBy: false,
          Cell: ({ value }) => {
            return <StatusBadge status={value?.toLowerCase() ?? "active"} />;
          },
        },
        {
          Header: "last edited",
          accessor: "updated_date_time",
          width: 100,
          canGroupBy: false,
          Cell: ({ value }) => (
            <SoftTypography
              sx={{
                fontSize: "12px",
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                fontWeight: "500",
              }}
            >
              <span>{value && moment(value).format("MM/DD/YYYY")}</span>
              <span>{value && moment(value).format("h:mm:ss A")}</span>
            </SoftTypography>
          ),
        },
        {
          Header: "cost",
          accessor: "cost",
          canGroupBy: false,
          Cell: ({ value }) => (
            <SoftTypography fontSize="14px" fontWeight="bold">
              {value}
            </SoftTypography>
          ),
        },
        {
          Header: "is owned",
          accessor: "is_owned",
          show: false,
        },
        {
          accessor: "id",
          show: false,
        },
        {
          accessor: "description",
          show: false,
        },
      ].map((column) => ({ ...column, disableResize: true })),
      rows: flowConnectors.filter(handleFilterConnector).map((component) => {
        const creatorId = component.connector_creator_id;
        const connectorCreator = creatorId ? connectorCreators[creatorId] : null;

        return {
          id: component.id,
          name: component.name,
          status: component.status,
          updated_date_time: component.updated_date_time ? component.updated_date_time : null,
          cost:
            component.price === null ? "Free" : component.price === 0 ? "Free" : component.price,
          is_owned: component.tenant_id === user.stripeId,
          logo: (
            <SoftBox
              component="img"
              width="100%"
              height="100%"
              borderRadius="12px"
              sx={{ objectFit: "contain" }}
              border={component.logo ? "0.5px solid #E6E6E6" : "none"}
              src={component.logo ? component.logo : defaultConnectorIcon}
            />
          ),
          developer: connectorCreator ? connectorCreator.company_name : "",
          developer_logo:
            connectorCreator?.logo && connectorCreator?.logo !== "null" ? (
              <SoftBox
                component="img"
                src={connectorCreator.logo}
                sx={{
                  width: "32px",
                  height: "32px",
                  objectFit: "contain",
                  borderRadius: "32px",
                }}
              />
            ) : (
              ""
            ),
          description: component.description,
        };
      }),
    };
  }, [connectorsWithCreator, flowConnectorIds, connectorCreators, searchName, user?.stripeId]);

  useEffect(() => {
    handleFetchConnectorsWithCreators(ownedComponents);
  }, [ownedComponents]);

  return (
    <>
      <SoftBox>
        <SoftBox
          sx={{
            alignItems: "flex-start",
            display: "flex",
            justifyContent: "flex-start",
            padding: "12px 0 12px 10px"
          }}
        >
          <SoftInput
            size="medium"
            placeholder="Search..."
            value={searchName}
            onChange={({ currentTarget }) => {
              setSearchName(currentTarget.value);
            }}
            sx={{
              maxWidth: "380px",
              marginRight: "34px",
            }}
          />
          {isAllowedAction(flow?.created_by_id, [
            PERMISSIONS.EDIT_WORKFLOWS,
            PERMISSIONS.EDIT_ANY_WORKFLOWS,
          ]) &&
            !isReadOnlyAccess && (
              <SoftButton variant="outlined" color="info" onClick={handleAddConnector}>
                add
              </SoftButton>
            )}
        </SoftBox>
        <SoftBox>
          <DataTable
            table={tableData}
            entriesPerPage={{ defaultValue: 25 }}
            hiddenTopButtons
            tableView={"list"}
            tableHeight="clamp(350px, calc(100vh - 130px), calc(100vh - 500px))"
          />
        </SoftBox>
      </SoftBox>
      <AddConnectorDialog
        openAddConnectorDialog={openAddConnectorDialog}
        onClose={() => setOpenAddConnectorDialog(false)}
        connectorsWithCreator={connectorsWithCreator}
        connectorCreators={connectorCreators}
      />
    </>
  );
};
